import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CurrentUserService } from './current-user.service';
import { AuthFeature, BrandProtectionFeature, Permission } from '../graphql/gql';

type AuthorizationPartial = {
  isAdmin: boolean,
  permissions: Array<Pick<Permission, 'subjectClass' | 'action' | 'requireTan'>>,
  featureAccess: Array<AuthFeature>,
  brandProtectionAccess: Array<BrandProtectionFeature>
}

/**
 * Known and used backend permission classes.
 */
export enum PermissionSubjectClass {
  AccessToken = 'AccessToken',
  ApprovalProcedure = "Approval::Procedure",
  BillingConfig = "BillingConfig",
  Brand = "Brand",
  Contact = "Contact",
  Department = "Department",
  DnsZone = "DnsZone",
  Document = "Document",
  Domain = "Domain",
  DomainGroup = "DomainGroup",
  Invoice = "Billing::Invoice",
  Monitoring = "Monitoring",
  Nameserver = "Nameserver",
  SocialMediaAccount = "SocialMediaAccount",
  SSLCertificate = "SSLCertificate",
  TldGroup = "TldGroup",
  Trademark = "Trademark",
  User = "User"
}

/**
 * Known and used backend permission actions.
 */
export enum PermittedAction {
  Create = 'create',
  Import = 'import',
  Manage = 'manage',
  Read = 'read',
  Register = 'register',
  Transfer = 'transfer',
  Update = 'update',
  UpdateDns = 'update_dns',
  Destroy = 'destroy'
}

/**
 * Decorator to declare a method to be overridden by `isAdmin`.
 *
 * If the instance's `isAdmin()` returns true, do not evaluate the original method, but always return true.
 */
function adminOverrides(_target: FeatureAccessService, _propertyKey: string, descriptor: PropertyDescriptor) {
  const originalValue = descriptor.value;

  descriptor.value = function(...args: any[]) {
    return (this as any).isAdmin() || originalValue.apply(this, args);
  }
}

/**
 * Service for managing feature access in the UI.
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureAccessService {
  private readonly BLANK_AUTHORIZATION: AuthorizationPartial = {
    isAdmin: false,
    permissions: [],
    featureAccess: [],
    brandProtectionAccess: []
  };

  private visibilityChangeSubject = new BehaviorSubject<boolean>(true);
  public visibilityChange$ = this.visibilityChangeSubject.asObservable();
  private authorization: AuthorizationPartial = this.BLANK_AUTHORIZATION;

  constructor(private currentUserService: CurrentUserService) {
    this.currentUserService.currentUser.subscribe(user => {
      if (user && user.currentUser) {
        this.authorization = user?.currentUser?.authorization;
      } else {
        this.authorization = this.BLANK_AUTHORIZATION;
      }

      this.visibilityChangeSubject.next(true);
    })
  }

  /**
   * Entrypoint for admin capabilities check.
   *
   * @todo This should eventually use a datum from the session instead of the user flags.
   */
  private isAdmin(): boolean {
    return this.authorization.isAdmin;
  }

  /**
   * Check if any permission matches the given `subjectClass`.
   *
   * @todo This does not grant the blanket acces to admins that is in place in the legacy UI. Since
   *   the new UI is only meant to be used by reseller clients, this allows for better user-perspective
   *   testing.
   */
  @adminOverrides
  private hasAnySubjectPermission(subjectClass: PermissionSubjectClass): boolean {
    return this.authorization.permissions.some((permission) => {
      return permission.subjectClass === subjectClass;
    });
  }

  hasFeatureAccess(featureName: AuthFeature): boolean {
    let hasFeature = this.authorization.featureAccess.includes(featureName)
    if(featureName === AuthFeature.Reseller) {
      return hasFeature
    }
    return this.isAdmin() || hasFeature
  }

  @adminOverrides
  canPerformAction(subjectClass: PermissionSubjectClass, action: PermittedAction): boolean {
    return this.authorization.permissions.some((permission) => {
      return permission.subjectClass === subjectClass &&
        permission.action === action;
    });
  }

  @adminOverrides
  canReadSubject(subjectClass: PermissionSubjectClass): boolean {
    return this.canPerformAction(subjectClass, PermittedAction.Read);
  }

  /**
   * General ability to impersonate other users.
   *
   * This is restricted to admins and users assigned as managers to other accounts.
   */
  canImpersonateUsers(): boolean {
    return this.isAdmin() || this.hasFeatureAccess(AuthFeature.AccountManagement);
  }

  @adminOverrides
  canOrderBrandMonitoring(feature: BrandProtectionFeature): boolean {
    return this.authorization.brandProtectionAccess.includes(feature);
  }

  /**
   * @todo This is taken from legacy, whatever kind of sense it makes.
   */
  canSeeWorkRequests(): boolean {
    return this.canPerformAction(PermissionSubjectClass.Domain, PermittedAction.Read);
  }

  canSeeAdminSection(): boolean {
    return this.isAdmin();
  }

  canSeeBrandsSection(): boolean {
    return this.hasAnySubjectPermission(PermissionSubjectClass.Brand);
  }

  canSeeTrademarksSection(): boolean {
    return this.hasAnySubjectPermission(PermissionSubjectClass.Trademark);
  }

  canSeeDomainsSection(): boolean {
    return this.hasAnySubjectPermission(PermissionSubjectClass.Domain);
  }

  canSeeInvoices(): boolean {
    return this.canSeePrices() && this.hasAnySubjectPermission(PermissionSubjectClass.Invoice);
  }

  canSeePrices(): boolean {
    return this.hasFeatureAccess(AuthFeature.Pricing);
  }

  canSeeResellerSection(): boolean {
    return this.hasFeatureAccess(AuthFeature.Reseller);
  }
}
